import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastInstrument,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import React, { FC, useCallback } from 'react';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import classNames from 'classnames';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import SvgInformation from '@app/shared/icons/Information';
import {
  formatValueForTargetMetric,
  getOperator,
  noEscapeValueInterpolationOption,
} from '@app/shared/helpers/localisation/localisation-options';
import {
  CalcMethod,
  IRRHurdleCalculationsNavigatedSourcePage,
} from '@app/shared/models/contracts/enums/shared-enums';
import InstrumentExitDistributionsHurdleDetails from '../instrument-calculations-erf/InstrumentExitDistributionsHurdleDetails';
import InstrumentExitDistributionsTotals from '../instrument-calculations-erf/InstrumentExitDistributionsTotals';
import TrancheCalculationConditionRow from './TrancheCalculationConditionRow';
import TrancheConditionsCell from './TrancheConditionsCell';
import TrancheCalculationRow from './TrancheCalculationRow';
import TrancheMoMRow from './TrancheMoMRow';
import { SubSectionHeader } from '../instrument-calculations-erf/row-components/SubSectionHeader';
import { simulationSectionsDefinition } from '../helper/FirstLastHelpers';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  INSTITUTIONAL_MOM_KEY,
  INSTITUTIONAL_IRR_KEY,
  METRIC_TRANSLATION_MAP,
  getTrancheDictionary,
  hasIrrOrMomHurdle,
  hasOnlyOrdinaryEquityTranche,
} from '../helper/TrancheCalculationsErfHelpers';
import { useIrrHurdleNavigation } from '@app/shared/hooks/useIrrHurdleNavigation';

interface TrancheCalculationsErfProps {
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedInstrument: string;
  selectedYear: string;
  onTrancheIndex?: (trancheIndex: number) => void;
  instrument: FirstLastInstrument;
  instrumentHasDloc?: boolean;
  firstSimulation: string;
}

const TrancheCalculationsErf: FC<TrancheCalculationsErfProps> = ({
  opmWaterfall,
  selectedInstrument,
  selectedYear,
  onTrancheIndex,
  instrument,
  instrumentHasDloc,
  firstSimulation,
}): JSX.Element => {
  const { l } = useLocale();

  const projectUsesOpm = useAppSelector(
    (state) =>
      state.project.projectDraft.details.calcMethod === CalcMethod.OPM ||
      state.project.projectDraft.details.calcMethod ===
        enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM)
  );

  const waterfallIterations = useAppSelector((state) => state.uiValues.opmWaterfallIterations ?? 5);
  const titleColSpan = waterfallIterations * 2 + 1;

  const trancheEntries = Object.entries(
    opmWaterfall[selectedYear].first[1].instruments[selectedInstrument].tranches
  );

  React.useEffect(() => {
    if (onTrancheIndex && trancheEntries.length > 0) {
      onTrancheIndex(0);
    }
  }, [onTrancheIndex, trancheEntries.length]);

  const handleNavigationClick = useIrrHurdleNavigation({
    selectedYear,
    sourcePage: IRRHurdleCalculationsNavigatedSourcePage.FirstLastWaterfall,
  });

  const handleClick = useCallback(
    (conditionReference: string, title: string) =>
      handleNavigationClick(selectedInstrument, conditionReference, title),
    [handleNavigationClick, selectedInstrument]
  );

  const getBoundTrancheDictionary = (firstOrLastFive: 'first' | 'last') =>
    getTrancheDictionary(opmWaterfall, selectedYear, selectedInstrument, firstOrLastFive);

  return (
    <>
      {trancheEntries.map((data, trancheIndex) => {
        const tranche = data[1];
        const trancheKey = data[0];
        const hasIrrOrMomHurdleResult = hasIrrOrMomHurdle(tranche.conditions);

        const firstValueConditions = getBoundTrancheDictionary('first').map(
          (trancheDict) => trancheDict[trancheKey].conditions
        );
        const lastValueConditions = getBoundTrancheDictionary('last').map(
          (trancheDict) => trancheDict[trancheKey].conditions
        );

        return (
          <React.Fragment key={`tranche-${trancheIndex}`}>
            <SubSectionHeader
              title={`${l('_Tranche')} ${trancheIndex + 1}`}
              className={styles['sub-section-header']}
              titleColSpan={titleColSpan}
              data-testid={`tranche-title-${trancheIndex}`}
            />
            {hasIrrOrMomHurdleResult && (
              <TrancheCalculationRow
                valueKey="sponsorInvestment"
                labelKey="_SponsorInvestment"
                trancheIndex={trancheIndex}
                opmWaterfall={opmWaterfall}
                selectedYear={selectedYear}
                selectedInstrument={selectedInstrument}
                waterfallIterations={waterfallIterations}
                getTrancheDictionary={getBoundTrancheDictionary}
                l={l}
              />
            )}
            {hasIrrOrMomHurdleResult &&
              tranche.conditions.some(
                (condition) =>
                  (condition.metric === INSTITUTIONAL_MOM_KEY &&
                    condition.cumulativeInfusions &&
                    condition.cumulativeInfusions !== 0) ||
                  (condition.metric === INSTITUTIONAL_IRR_KEY &&
                    condition.cumulativeInfusions &&
                    condition.cumulativeInfusions !== 0)
              ) && (
                <>
                  <TrancheCalculationConditionRow
                    valueKey="cumulativeInfusions"
                    label={l('_AddCumulativeInfusion')}
                    trancheIndex={trancheIndex}
                    bold={false}
                    opmWaterfall={opmWaterfall}
                    selectedYear={selectedYear}
                    selectedInstrument={selectedInstrument}
                    waterfallIterations={waterfallIterations}
                    getTrancheDictionary={getBoundTrancheDictionary}
                  />

                  <TrancheCalculationConditionRow
                    valueKey="totalSponsorCost"
                    label={l('_TotalSponsorCost')}
                    trancheIndex={trancheIndex}
                    bold
                    opmWaterfall={opmWaterfall}
                    selectedYear={selectedYear}
                    selectedInstrument={selectedInstrument}
                    waterfallIterations={waterfallIterations}
                    getTrancheDictionary={getBoundTrancheDictionary}
                  />
                </>
              )}
            {tranche.conditions.map((condition, conditionIndex) => {
              const sponsorHurdleLabel = `${l('_SponsorConditionPlaceholders', {
                metric: METRIC_TRANSLATION_MAP[condition.metric]
                  ? l(METRIC_TRANSLATION_MAP[condition.metric])
                  : condition.metric,
                operator: getOperator(condition.operator),
                value: formatValueForTargetMetric(condition.metric, condition.testValue),
                interpolation: noEscapeValueInterpolationOption,
              })}`;

              return (
                <React.Fragment key={`condition-${conditionIndex}`}>
                  <tr>
                    <td
                      className={styles['border-right']}
                      data-testid={`condition${conditionIndex + 1}-tranche${
                        trancheIndex + 1
                      }-title`}>
                      <div className={styles['tooltip-header']}>
                        {sponsorHurdleLabel}
                        {projectUsesOpm && condition.metric === INSTITUTIONAL_IRR_KEY && (
                          <Tooltip
                            content={l('_IrrNumbersCanBeClickedForFurtherInformation')}
                            placement={TooltipPlacement.Right}
                            useFixedPositioning>
                            <Button
                              appearance={ButtonAppearance.CLEAN}
                              size={ButtonSize.FLUID}
                              aria-label={l('_IrrNumbersCanBeClickedForFurtherInformation')}>
                              <SvgInformation className={styles['warning-icon']} />
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    {firstValueConditions.map((conditions, index) => {
                      const conditionReference =
                        conditions[conditionIndex].metric +
                        ' - ' +
                        conditions[conditionIndex].testValue;
                      return (
                        <TrancheConditionsCell
                          key={`non-mom-condition-${conditionIndex}-${index}`}
                          conditionIndex={conditionIndex}
                          index={index}
                          trancheIndex={trancheIndex}
                          includeRightBorder={index + 1 === waterfallIterations}
                          conditionReference={
                            projectUsesOpm && condition.metric === INSTITUTIONAL_IRR_KEY
                              ? conditionReference
                              : undefined
                          }
                          sponsorHurdleLabel={sponsorHurdleLabel}
                          onHandleClick={handleClick}
                          value={
                            condition.metric === INSTITUTIONAL_MOM_KEY
                              ? conditions[conditionIndex].momHurdleDetails?.rawSponsorHurdleMom ??
                                0
                              : conditions[conditionIndex].equivalentHurdleValue
                          }
                        />
                      );
                    })}
                    {lastValueConditions.map((conditions, index) => {
                      const conditionReference =
                        conditions[conditionIndex].metric +
                        ' - ' +
                        conditions[conditionIndex].testValue;
                      return (
                        <TrancheConditionsCell
                          key={`non-mom-condition-${conditionIndex}-last-${index}`}
                          conditionIndex={conditionIndex}
                          index={index}
                          trancheIndex={trancheIndex}
                          includeRightBorder={false}
                          conditionReference={
                            projectUsesOpm && condition.metric === INSTITUTIONAL_IRR_KEY
                              ? conditionReference
                              : undefined
                          }
                          sponsorHurdleLabel={sponsorHurdleLabel}
                          onHandleClick={handleClick}
                          value={
                            condition.metric === INSTITUTIONAL_MOM_KEY
                              ? conditions[conditionIndex].momHurdleDetails?.rawSponsorHurdleMom ??
                                0
                              : conditions[conditionIndex].equivalentHurdleValue
                          }
                        />
                      );
                    })}
                  </tr>
                  <React.Fragment key={`mom-details-${conditionIndex}`}>
                    {condition.metric === INSTITUTIONAL_MOM_KEY && (
                      <>
                        {condition.momHurdleDetails?.cumulativeRedemptions !== 0 && (
                          <TrancheMoMRow
                            valueKey="cumulativeRedemptions"
                            label={l('_LessCumulativeRedemption')}
                            trancheIndex={trancheIndex}
                            bold={false}
                            conditionIndex={conditionIndex}
                            opmWaterfall={opmWaterfall}
                            selectedYear={selectedYear}
                            selectedInstrument={selectedInstrument}
                            waterfallIterations={waterfallIterations}
                            getTrancheDictionary={getBoundTrancheDictionary}
                          />
                        )}
                        {condition.momHurdleDetails?.cumulativeCashPaid !== 0 && (
                          <TrancheMoMRow
                            valueKey="cumulativeCashPaid"
                            label={l('_LessCumulativeCashInterest')}
                            trancheIndex={trancheIndex}
                            bold={false}
                            conditionIndex={conditionIndex}
                            opmWaterfall={opmWaterfall}
                            selectedYear={selectedYear}
                            selectedInstrument={selectedInstrument}
                            waterfallIterations={waterfallIterations}
                            getTrancheDictionary={getBoundTrancheDictionary}
                          />
                        )}
                        {condition.momHurdleDetails?.cumulativeDividends !== 0 && (
                          <TrancheMoMRow
                            valueKey="cumulativeDividends"
                            label={l('_LessCumulativeDividends')}
                            trancheIndex={trancheIndex}
                            bold={false}
                            conditionIndex={conditionIndex}
                            opmWaterfall={opmWaterfall}
                            selectedYear={selectedYear}
                            selectedInstrument={selectedInstrument}
                            waterfallIterations={waterfallIterations}
                            getTrancheDictionary={getBoundTrancheDictionary}
                          />
                        )}

                        <tr>
                          <td className={classNames(styles['border-right'], styles['bold-text'])}>
                            {'Net ' + sponsorHurdleLabel}
                          </td>
                          {simulationSectionsDefinition.map((section) =>
                            Object.entries(opmWaterfall[selectedYear][section]).map((_, index) => {
                              const conditions = tranche.conditions;
                              return (
                                <TrancheConditionsCell
                                  key={`net-condition-${conditionIndex}-${section}-${index}`}
                                  conditionIndex={conditionIndex}
                                  index={index}
                                  trancheIndex={trancheIndex}
                                  includeRightBorder={index + 1 === waterfallIterations}
                                  value={conditions[conditionIndex].equivalentHurdleValue}
                                />
                              );
                            })
                          )}
                        </tr>
                      </>
                    )}
                  </React.Fragment>
                </React.Fragment>
              );
            })}
            {hasOnlyOrdinaryEquityTranche(
              opmWaterfall[selectedYear].first[1].instruments[selectedInstrument].tranches
            ) && (
              <tr>
                <td className={styles['border-right']}>{l('_TotalOrdinaryEquity')}</td>
                {simulationSectionsDefinition.map((section) =>
                  Object.entries(opmWaterfall[selectedYear][section]).map(
                    ([simulationKey, summary], index) => {
                      const value = summary.totalOrdinaryEquityProceeds ?? null;
                      return (
                        <td
                          key={`total-ordinary-equity-proceeds-value-${section}-${simulationKey}`}
                          className={classNames({
                            [styles['border-right']]: index + 1 === waterfallIterations,
                          })}
                          data-testid={`totalOrdinaryEquityProceeds-${section}-simulation-${simulationKey}`}>
                          <CellValue value={value} {...amountRules} />
                        </td>
                      );
                    }
                  )
                )}
              </tr>
            )}
            <InstrumentExitDistributionsHurdleDetails
              opmWaterfall={opmWaterfall}
              selectedYear={selectedYear}
              waterfallIterations={waterfallIterations}
              instrumentHasTranches
              instrumentKey={selectedInstrument}
              instrument={instrument}
              currentTrancheIndex={trancheIndex}
            />
            <InstrumentExitDistributionsTotals
              instrumentHasTranches
              instrumentName={instrument.instrumentName}
              interpolationOption={noEscapeValueInterpolationOption}
              opmWaterfall={opmWaterfall}
              selectedYear={selectedYear}
              firstSimulation={firstSimulation}
              waterfallIterations={waterfallIterations}
              instrumentKey={selectedInstrument}
              currentTrancheIndex={trancheIndex}
              instrumentHasDloc={instrumentHasDloc}
            />
          </React.Fragment>
        );
      })}
    </>
  );
};

export default TrancheCalculationsErf;
